import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Loader2, AlertTriangle, Clapperboard, Image as ImageIcon, Layers, History, Clock, X } from "lucide-react";
import type { MediaJob } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTruncated } from "@/lib/useTruncated";

/** Label KEYS per job kind — module-level, so it cannot call a hook. */
const KIND_LABEL = {
  video_cut: { labelKey: "shell.videoCut", Icon: Clapperboard },
  thumbnail: { labelKey: "shell.previewGenerated", Icon: ImageIcon },
  collage: { labelKey: "shell.collageBuilt", Icon: Layers },
} as const satisfies Record<MediaJob["kind"], { labelKey: string; Icon: typeof Clapperboard }>;

/** Dot + wording per phase. Glyph AND text AND tint — never colour alone (the repo a11y rule). */
const PHASE = {
  queued: { dot: "bg-muted-foreground/40", labelKey: "shell.jobQueued" },
  processing: { dot: "bg-attention", labelKey: "shell.jobProcessing" },
  finished: { dot: "bg-success", labelKey: "shell.jobFinished" },
  failed: { dot: "bg-destructive", labelKey: "shell.jobFailed" },
} as const;

/**
 * Background media jobs, as a right-side drawer timeline (ADR-026 Phase 6; reworked 2026-09-02 on owner report).
 *
 * **The trigger is PERMANENT.** It used to render `null` when the list was empty, which failed the one job it had: the
 * owner came back the morning after cutting five videos, found no icon at all, and could not tell "nothing to show"
 * from "the feature is broken" — they asked me, which is the proof. An affordance that vanishes when empty cannot be
 * used to check whether it is empty. The icon is quiet at rest (a history glyph, no count) and only becomes loud —
 * spinner, then a count — while work is actually running.
 *
 * **A drawer, not a popover.** The list carries real provenance now (manual → section → step), which is two lines a
 * row; a 288px popover truncated all of it. The drawer also survives scrolling and gives the timeline room to group by
 * day, so "what did I cut yesterday?" is answered by the shape of the thing, not by reading timestamps.
 */
export function BackgroundJobs({ jobs, isLoading = false, isError = false, onRetry }: { jobs: MediaJob[]; isLoading?: boolean; isError?: boolean; onRetry?: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const active = jobs.filter((j) => j.status === "pending" || j.status === "enqueued");
  const failed = jobs.filter((j) => j.status === "error");

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5"
        onPress={() => setOpen(true)}
        aria-label={
          active.length > 0
            ? t("shell.backgroundJobsRunning", { count: active.length })
            : failed.length > 0
              ? t("shell.backgroundJobsFailed", { count: failed.length })
              : t("shell.backgroundJobs")
        }
      >
        {/* Spinner ONLY while something is actually running — a spinner that never stops trains people to ignore it.
            `prefers-reduced-motion` is honoured by the shared animate utility. */}
        {active.length > 0 ? (
          <Loader2 aria-hidden="true" className="size-4 animate-spin" />
        ) : failed.length > 0 ? (
          <AlertTriangle aria-hidden="true" className="text-destructive size-4" />
        ) : (
          <History aria-hidden="true" className="size-4" />
        )}
        {active.length > 0 ? <span className="text-sm tabular-nums">{active.length}</span> : null}
      </Button>

      {open ? (
        <Drawer isOpen isDismissable onOpenChange={(isOpen) => !isOpen && setOpen(false)}>
          <DrawerHeader>
            <div className="min-w-0">
              <DrawerTitle className="text-base">{t("shell.backgroundJobs")}</DrawerTitle>
              <p className="text-muted-foreground text-xs">
                {active.length > 0 ? t("shell.runningPrefix", { count: active.length }) : ""}
                {t("shell.lastSevenDays")}
              </p>
            </div>
            {/* Top-right close, same as every other drawer (`DrawerHeader` lays out title | actions). Esc and the
                backdrop already dismiss, but a visible control is the one people look for — and the only one that is
                obvious on a touch screen. */}
            <Button variant="ghost" size="icon-sm" aria-label={t("shell.close")} onPress={() => setOpen(false)}>
              <X className="size-4" />
            </Button>
          </DrawerHeader>
          {isError ? (
            <ErrorState onRetry={onRetry} />
          ) : isLoading ? (
            <LoadingState />
          ) : jobs.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="-mr-2 flex-1 overflow-y-auto pr-2">
              {groupByDay(jobs).map(([day, dayJobs]) => (
                <section key={day} className="mb-5 last:mb-0">
                  {/* The day heading IS the timeline's structure — it turns a flat log into "yesterday I cut these". */}
                  <h3 className="text-muted-foreground bg-card sticky top-0 z-10 py-1 text-xs font-medium">{day}</h3>
                  <ol className="border-border relative m-0 list-none border-l p-0 pl-4">
                    {dayJobs.map((job) => (
                      <JobEntry key={job.id} job={job} />
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          )}
        </Drawer>
      ) : null}
    </>
  );
}

/** One node on the timeline: what happened, where it came from, and when. */
function JobEntry({ job }: { job: MediaJob }) {
  const { t } = useTranslation();
  const meta = KIND_LABEL[job.kind];
  const Icon = meta.Icon;
  const phase = PHASE[job.phase];
  return (
    <li className="relative pb-4 last:pb-0">
      {/* The dot sits ON the rail (the ol's left border), which is what reads as a timeline rather than a bullet list. */}
      <span aria-hidden="true" className={cn("border-card absolute -left-[1.4rem] top-1.5 size-2.5 rounded-full border-2", phase.dot)} />
      <div className="flex items-start gap-2">
        <Icon aria-hidden="true" className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
            <span>{t(meta.labelKey)}</span>
            {/* Phase in WORDS as well as colour — the dot alone is invisible to a screen reader and to anyone who
                cannot separate the two tints. */}
            <span className={cn("text-xs", job.phase === "failed" ? "text-destructive" : "text-muted-foreground")}>{t(phase.labelKey)}</span>
          </p>
          {/* WHERE it came from. The breadcrumb is the whole point of the rework: a row that says only "Video cut" is
              useless when several are queued against different manuals. */}
          <Provenance job={job} />
          {job.failedReason ? <p className="text-destructive mt-0.5 text-xs">{job.failedReason}</p> : null}
        </div>
        {/* The full timestamp on hover — via the shared Tooltip, never a native `title` (the repo rule: `title` is
            slow to appear and cannot be themed). */}
        <TooltipTrigger delay={0} excludeFromTabOrder>
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
            {job.phase === "queued" || job.phase === "processing" ? <Clock aria-hidden="true" className="inline size-3" /> : null}
            <span className="ml-1">{timeOfDay(job.createdAt)}</span>
          </span>
          <Tooltip>{new Date(job.createdAt).toLocaleString()}</Tooltip>
        </TooltipTrigger>
      </div>
    </li>
  );
}

/** Manual → section → step, skipping whatever the job does not have (a thumbnail has no step at all). */
function Provenance({ job }: { job: MediaJob }) {
  const { t } = useTranslation();
  const parts = [job.manualName, job.sectionName, job.stepName].filter((p): p is string => p != null && p !== "");
  const full = parts.join(" · ");
  const { ref, isTruncated, measure } = useTruncated<HTMLParagraphElement>(full);
  // A job whose payload records no target at all — an orphan row, or a kind that has no step (a bare thumbnail).
  // SAY so rather than rendering an empty line: a blank where every neighbour has a breadcrumb reads as a rendering
  // bug, which is precisely how the owner read it.
  if (parts.length === 0) return <p className="text-muted-foreground/70 mt-0.5 text-xs italic">{t("shell.noTargetRecorded")}</p>;
  return (
    // Tooltip ONLY when the trail is actually cut off — a hint that repeats what you can already read is noise.
    <TooltipTrigger delay={0} isDisabled={!isTruncated} excludeFromTabOrder>
      <p ref={ref} onPointerEnter={measure} className="text-muted-foreground mt-0.5 truncate text-xs">
        {parts.map((part, i) => (
          <span key={`${part}-${String(i)}`}>
            {i > 0 ? <span aria-hidden="true"> · </span> : null}
            {part}
          </span>
        ))}
      </p>
      <Tooltip>{full}</Tooltip>
    </TooltipTrigger>
  );
}

/** The read failed — say THAT, and offer the retry. Never the "nothing in 7 days" claim, which would be a lie. */
function ErrorState({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
      <span className="bg-destructive/10 text-destructive flex size-10 items-center justify-center rounded-full">
        <AlertTriangle aria-hidden="true" className="size-5" />
      </span>
      <p className="text-sm font-medium">{t("shell.couldNotLoadJobs")}</p>
      <p className="text-muted-foreground max-w-[26ch] text-xs">{t("shell.jobsConnectionProblem")}</p>
      {onRetry ? (
        <Button variant="outline" size="sm" className="dark:border-border mt-1" onPress={onRetry}>
          {t("shell.tryAgain")}
        </Button>
      ) : null}
    </div>
  );
}

/** Still asking. Deliberately NOT the empty state: an empty answer we do not have yet is not an answer. */
function LoadingState() {
  const { t } = useTranslation();
  return (
    <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 text-center">
      <Loader2 aria-hidden="true" className="size-5 animate-spin" />
      <p className="text-xs">{t("shell.loadingYourJobs")}</p>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
      <span className="bg-muted text-muted-foreground flex size-10 items-center justify-center rounded-full">
        <History aria-hidden="true" className="size-5" />
      </span>
      {/* Say the HORIZON, not just "nothing". "No jobs" invites "…or is it broken?" — the exact doubt that produced
          this rework; naming the window makes the emptiness a fact about the last 7 days. */}
      <p className="text-sm font-medium">{t("shell.noBackgroundJobs")}</p>
      <p className="text-muted-foreground max-w-[24ch] text-xs">{t("shell.nothingInSevenDays")}</p>
    </div>
  );
}

/** `HH:MM` in the viewer's locale — the day is already the group heading, so the row only needs the time. */
function timeOfDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/**
 * Group into calendar days, newest first, preserving the server's ordering within each day.
 *
 * Grouped on `createdAt` — when the person STARTED the work, which is how they remember it ("I cut that before lunch"),
 * not when the worker happened to finish.
 */
export function groupByDay(jobs: MediaJob[]): Array<[string, MediaJob[]]> {
  const groups = new Map<string, MediaJob[]>();
  for (const job of jobs) {
    const label = dayLabel(job.createdAt);
    const bucket = groups.get(label);
    if (bucket) bucket.push(job);
    else groups.set(label, [job]);
  }
  return [...groups.entries()];
}

/** "Today" / "Yesterday" / a written date — relative labels for the two days people actually think in. */
function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Earlier";
  const startOf = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(new Date()) - startOf(d)) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}
